import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Public lead-capture modal used on the Landing page CTAs.
 * Posts to /api/leads (no auth required) and shows a thank-you state.
 */
export default function LeadCaptureDialog({ open, onOpenChange, defaultPlan = "annual_100", source = "landing" }) {
  const [planChoice, setPlanChoice] = useState(defaultPlan);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const close = () => {
    onOpenChange(false);
    setTimeout(() => {
      setSubmitted(false);
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
      setPlanChoice(defaultPlan);
    }, 300);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/leads", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        company: form.company.trim(),
        plan_interest: planChoice,
        message: `[Source: ${source}] ${form.message.trim()}`.trim(),
      });
      setSubmitted(true);
      toast.success("Got it — we'll reach out within 24 hours.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => v ? onOpenChange(true) : close()}>
      <DialogContent className="rounded-none border border-[#e5e5e5] max-w-lg bg-white">
        {!submitted ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display font-bold tracking-tight text-3xl flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-[#FF4D6D]" /> Book a 10-min demo
              </DialogTitle>
              <DialogDescription className="text-base text-[#52525b]">
                Leave your details, pick a plan that fits, and we&apos;ll get in touch within 24 hours.
              </DialogDescription>
            </DialogHeader>

            {/* Plan toggle */}
            <div className="grid grid-cols-2 gap-3 mt-2">
              {[
                { id: "monthly_10", price: "€10", per: "/ month", note: "Cancel any time" },
                { id: "annual_100", price: "€100", per: "/ year",  note: "Save ~17% · €8.33/mo" },
              ].map((p) => {
                const selected = planChoice === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPlanChoice(p.id)}
                    data-testid={`landing-plan-${p.id}`}
                    className={`relative text-left rounded-none border p-4 transition-colors ${
                      selected
                        ? "border-[#FF4D6D] bg-[#FFE4E9] text-[#1B1340]"
                        : "border-[#e5e5e5] bg-white text-[#0a0a0a] hover:border-[#0a0a0a]"
                    }`}
                  >
                    {p.id === "annual_100" && (
                      <span className="absolute -top-2 right-3 text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 bg-[#FF4D6D] text-white">
                        Best value
                      </span>
                    )}
                    <div className="text-2xl font-display font-medium leading-none">
                      {p.price}
                      <span className="text-xs ml-1 font-mono text-[#71717a]">{p.per}</span>
                    </div>
                    <div className="text-xs mt-2 text-[#71717a]">{p.note}</div>
                  </button>
                );
              })}
            </div>

            <form onSubmit={submit} className="space-y-3 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.18em] text-[#71717a] mb-1">Your name *</label>
                  <input
                    data-testid="landing-lead-name"
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full border border-[#e5e5e5] rounded-none px-3 h-10 text-sm focus:outline-none focus:border-[#FF4D6D]"
                    placeholder="Jane Smith"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.18em] text-[#71717a] mb-1">Brand</label>
                  <input
                    data-testid="landing-lead-company"
                    type="text"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    className="w-full border border-[#e5e5e5] rounded-none px-3 h-10 text-sm focus:outline-none focus:border-[#FF4D6D]"
                    placeholder="Belle Vue"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.18em] text-[#71717a] mb-1">Email *</label>
                  <input
                    data-testid="landing-lead-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full border border-[#e5e5e5] rounded-none px-3 h-10 text-sm focus:outline-none focus:border-[#FF4D6D]"
                    placeholder="jane@bellevue.com"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.18em] text-[#71717a] mb-1">Phone (optional)</label>
                  <input
                    data-testid="landing-lead-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full border border-[#e5e5e5] rounded-none px-3 h-10 text-sm focus:outline-none focus:border-[#FF4D6D]"
                    placeholder="+1 555 123 4567"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.18em] text-[#71717a] mb-1">Anything we should know? (optional)</label>
                <textarea
                  data-testid="landing-lead-message"
                  rows={3}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full border border-[#e5e5e5] rounded-none px-3 py-2 text-sm focus:outline-none focus:border-[#FF4D6D] resize-none"
                  placeholder="Number of stores, integrations needed, timeline…"
                />
              </div>
              <p className="text-xs text-[#a1a1aa]">No payment now. You&apos;ll hear from us within 24h.</p>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={close} className="rounded-none border-[#e5e5e5] h-11 px-5">
                  Maybe later
                </Button>
                <Button
                  type="submit"
                  disabled={busy}
                  data-testid="landing-lead-submit"
                  className="rounded-none bg-[#FF4D6D] hover:bg-[#E03A5A] text-white h-11 px-5"
                >
                  {busy ? "Sending…" : "Book demo"}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display font-bold tracking-tight text-3xl flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-[#FF4D6D]" /> Thanks — we&apos;ll be in touch
              </DialogTitle>
              <DialogDescription className="text-base text-[#52525b]">
                We received your request for the <span className="font-medium text-[#1B1340]">{planChoice === "monthly_10" ? "€10/month" : "€100/year"}</span> plan. We&apos;ll email <span className="font-medium text-[#1B1340]">{form.email}</span> within 24 hours.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={close} data-testid="landing-lead-done" className="rounded-none bg-[#FF4D6D] hover:bg-[#E03A5A] text-white h-11 px-5">
                Got it
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
